import React from 'react';

import Player from './Player';
import players from './players';
import { FilePlayer } from './players/FilePlayer';
import renderPreloadPlayers from './preload';
import Preview from './Preview';
import { defaultProps, DEPRECATED_CONFIG_PROPS, propTypes } from './props';
import type { Config, ReactPlayerProps, ReactPlayerState, SourceProps } from './types';
import { getConfig, isEqual, omit } from './utils';

const SUPPORTED_PROPS = Object.keys(propTypes);

let customPlayers: ReactPlayer[] = [];

export class ReactPlayer extends React.Component<ReactPlayerProps, ReactPlayerState> {
  static addCustomPlayer = (player: ReactPlayer) => {
    customPlayers.push(player);
  };

  static removeCustomPlayers = () => {
    customPlayers = [];
  };

  static displayName = 'ReactPlayer';

  static propTypes = propTypes;

  static defaultProps = defaultProps;

  public static canPlay = (url: string) => {
    const allPlayers: ReactPlayer[] = [...customPlayers, ...(players as unknown as ReactPlayer[])];
    for (const Player of allPlayers) {
      // @ts-expect-error - TS can't access own static methods
      if (Player.canPlay(url)) {
        return true;
      }
    }
    return false;
  };

  static canEnablePIP = (url: string) => {
    const allPlayers: ReactPlayer[] = [...customPlayers, ...(players as unknown as ReactPlayer[])];
    for (const Player of allPlayers) {
      // @ts-expect-error - TS can't access own static methods
      if (Player.canEnablePIP && Player.canEnablePIP(url)) {
        return true;
      }
    }
    return false;
  };

  private config: Config;

  private wrapper: any;

  private player: any;

  constructor(props: ReactPlayerProps) {
    super(props);
    this.config = getConfig(props, defaultProps, true);
    this.state = {
      showPreview: !!props.light,
    };
  }

  componentDidMount() {
    if (this.props.progressFrequency) {
      const message =
        'ReactPlayer: %cprogressFrequency%c is deprecated, please use %cprogressInterval%c instead';
      console.warn(message, 'font-weight: bold', '', 'font-weight: bold', '');
    }
  }

  shouldComponentUpdate(nextProps: ReactPlayerProps, nextState: ReactPlayerState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  componentDidUpdate(prevProps: ReactPlayerProps) {
    const { light } = this.props;
    this.config = getConfig(this.props, defaultProps);
    if (!prevProps.light && light) {
      this.setState({ showPreview: true });
    }
    if (prevProps.light && !light) {
      this.setState({ showPreview: false });
    }
  }

  handleClickPreview = () => {
    this.setState({ showPreview: false });
  };

  showPreview = () => {
    this.setState({ showPreview: true });
  };

  getDuration = () => {
    if (!this.player) return null;
    return this.player.getDuration();
  };

  getCurrentTime = () => {
    if (!this.player) return null;
    return this.player.getCurrentTime();
  };

  getSecondsLoaded = () => {
    if (!this.player) return null;
    return this.player.getSecondsLoaded();
  };

  getInternalPlayer = (key = 'player') => {
    if (!this.player) return null;
    return this.player.getInternalPlayer(key);
  };

  seekTo = (fraction: number, type?: 'seconds' | 'fraction') => {
    if (!this.player) return null;
    this.player.seekTo(fraction, type);
  };

  handleReady = () => {
    this.props.onReady?.();
  };

  getActivePlayer(url: $TSFixMe) {
    const allPlayers: ReactPlayer[] = [...customPlayers, ...(players as unknown as ReactPlayer[])];
    for (const Player of allPlayers) {
      // @ts-expect-error - TS can't access own static methods
      if (Player.canPlay(url)) {
        return Player;
      }
    }
    // Fall back to FilePlayer if nothing else can play the URL
    return FilePlayer;
  }

  wrapperRef = (wrapper: any) => {
    this.wrapper = wrapper;
  };

  activePlayerRef = (player: $TSFixMe) => {
    this.player = player;
  };

  renderActivePlayer(url?: string | string[] | SourceProps[] | MediaStream, activePlayer?: any) {
    if (!url) return null;
    return (
      <Player
        {...this.props}
        key={activePlayer.displayName}
        ref={this.activePlayerRef}
        config={this.config}
        activePlayer={activePlayer}
        onReady={this.handleReady}
      />
    );
  }

  sortPlayers(a: $TSFixMe, b: $TSFixMe) {
    // Retain player order to prevent weird iframe behaviour when switching players
    if (a && b) {
      return a.key < b.key ? -1 : 1;
    }
    return 0;
  }

  render() {
    const {
      url,
      controls,
      style,
      width,
      height,
      light,
      playIcon,
      wrapper: Wrapper,
      previewTabIndex,
      previewAriaLabel,
    } = this.props;
    const showPreview = this.state.showPreview && url;
    const otherProps = omit(this.props, SUPPORTED_PROPS, DEPRECATED_CONFIG_PROPS);
    const activePlayer = this.getActivePlayer(url);
    const renderedActivePlayer = this.renderActivePlayer(url, activePlayer);
    const preloadPlayers = renderPreloadPlayers(url, controls, this.config);
    const players = [renderedActivePlayer, ...preloadPlayers].sort(this.sortPlayers);
    const preview = (
      <Preview
        url={url}
        light={light}
        playIcon={playIcon}
        onClick={this.handleClickPreview}
        previewTabIndex={previewTabIndex}
        previewAriaLabel={previewAriaLabel}
      />
    );
    return (
      <Wrapper ref={this.wrapperRef} style={{ ...style, width, height }} {...otherProps}>
        {showPreview ? preview : players}
      </Wrapper>
    );
  }
}
